import { SimpleGrid, VStack, Box, Center } from '@chakra-ui/react';
import React, { ReactElement, useState, useEffect } from 'react';
import { NoContentTextWrapper } from 'components/common/NoContentTextWrapper';
import { PageContext } from 'components/common/PageContext';
import {
  MedicationSearchResult,
  ParsedArticleSearchResult,
  SearchContentType,
} from '@novaheal/types';
import { VideoSearchResult } from '@novaheal/types/lib/search/videoSearch';
import { SearchResult } from 'components/Search/SearchResult';
import { MedicationCard } from 'components/common/MedicationCard';
import placeholderImg from 'assets/common/placeholder.png';
import { VideoCardSkeleton } from 'components/atoms/VideoCardSkeleton';
import { Pagination } from 'components/common/Table/Pagination';
import { useAlgoliaContent } from 'hooks/api/backend/search/useAlgoliaContent';
import { ModalVideoCard } from './ModalVideoCard';

const MODAL_SEARCH_BATCH_SIZE = 12;

export const ModalSearchResults = ({
  onSelect,
  query,
}: {
  query: string;
  onSelect?: (data: {
    videoUrl: string;
    title: string;
    description?: string;
    thumbnailUrl?: string;
    qrCodeUrl?: string;
  }) => void;
}): ReactElement => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [skeletonKeys] = useState(() =>
    Array.from({ length: 6 }, (_, i) => `search-skeleton-${i}-${Math.random()}`)
  );
  const { data, isLoading } = useAlgoliaContent(
    [SearchContentType.VIDEO],
    query || '',
    currentPage,
    false,
    MODAL_SEARCH_BATCH_SIZE
  );

  // Reset to page 1 when query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [query]);

  // Get current page results
  const currentResults = data?.hits || [];
  const totalPages = data?.nbPages || 0;

  const handlePageChange = (newPage: number): void => {
    setCurrentPage(newPage);
  };

  return (
    <PageContext title="Suche" hasSearch headTitle="Suchergebnisse">
      {currentResults.length > 0 ? (
        <VStack flex={1} maxHeight="100%" overflow="hidden" gap="0" width="100%">
          <Box flexGrow={1} minWidth="100%" overflow="auto">
            <SimpleGrid
              columns={{ base: 2, lg: 3 }}
              gap="l"
              width="100%"
              maxWidth="100%"
              flexWrap="wrap"
              flex="1"
            >
              {isLoading
                ? skeletonKeys.map((key) => <VideoCardSkeleton key={key} />)
                : currentResults.map((parsedItem) => (
                    <React.Fragment key={parsedItem.objectID}>
                      {renderBasedOnType(parsedItem as SearchResultWithParsedArticle, onSelect)}
                    </React.Fragment>
                  ))}
            </SimpleGrid>
          </Box>
          <Box pt="l">
            <Pagination
              currentPage={currentPage}
              onPageChange={handlePageChange}
              totalPages={totalPages}
              noPadding
            />
          </Box>
        </VStack>
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <></>
      )}

      {query && currentResults.length === 0 && !isLoading ? (
        <Center width="100%" height="100%">
          <NoContentTextWrapper>
            Leider wurden keine Videos für &quot;{query}&quot; gefunden.
          </NoContentTextWrapper>
        </Center>
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <></>
      )}
    </PageContext>
  );
};

const renderBasedOnType = (
  result: SearchResultWithParsedArticle,
  onSelect?: (data: {
    videoUrl: string;
    title: string;
    description?: string;
    thumbnailUrl?: string;
    qrCodeUrl?: string;
  }) => void
): ReactElement | null => {
  if (isArticleSearchResult2(result)) {
    return (
      <SearchResult
        key={result.objectID}
        id={result.objectID}
        title={result.fields.title}
        imageUrl={result.fields.coverImage?.url || placeholderImg}
        subtitle={result.fields.category.name}
        hasVideo={result.fields.hasVideo}
        relevantYearNames={result.fields.relevantYears}
        description={result.highlightData?.value}
        csId={result.highlightData?.csId}
      />
    );
  }
  if (isMedicationSearchResult2(result)) {
    return (
      <MedicationCard
        key={result.objectID}
        id={result.objectID}
        name={result.fields.name}
        trademark={result.fields.trademark}
        indications={result.fields.indications}
      />
    );
  }
  if (isVideoSearchResult2(result)) {
    return (
      <ModalVideoCard
        key={result.objectID}
        title={result.fields.title}
        thumbnailUrl={result.fields.thumbnailUrl}
        description={result.fields.description}
        videoUrl={result.fields.videoUrl}
        qrCodeUrl={result.fields.qrCodeUrl}
        onSelect={onSelect}
      />
    );
  }
  return null;
};

type SearchResultWithParsedArticle =
  | MedicationSearchResult
  | ParsedArticleSearchResult
  | VideoSearchResult;

const isArticleSearchResult2 = (
  result: SearchResultWithParsedArticle
): result is ParsedArticleSearchResult => result.contentType === SearchContentType.ARTICLE;

const isMedicationSearchResult2 = (
  result: SearchResultWithParsedArticle
): result is MedicationSearchResult => result.contentType === SearchContentType.MEDICATION;

const isVideoSearchResult2 = (result: SearchResultWithParsedArticle): result is VideoSearchResult =>
  result.contentType === SearchContentType.VIDEO;
